
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';

interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

const defaultConsent: CookieConsent = {
  essential: true, // Essential cookies are always required
  analytics: false,
  marketing: false,
  timestamp: new Date().toISOString()
};

// Export this function so it can be used in other components
export function hasConsented(category: keyof Omit<CookieConsent, 'timestamp'>): boolean {
  const savedConsent = localStorage.getItem('cookie-consent');
  if (!savedConsent) return false;
  
  try {
    const parsedConsent = JSON.parse(savedConsent);
    // Ensure we convert to boolean in case the value is stored as a string
    return parsedConsent[category] === true;
  } catch (err) {
    return false;
  }
}

// Cookie Preferences component for the footer
export function CookiePreferences() {
  const [showSettings, setShowSettings] = useState(false);
  
  return (
    <>
      <button 
        onClick={() => setShowSettings(true)} 
        className="hover:text-[#2C6E49] transition-colors"
      >
        Cookie-Einstellungen
      </button>
      <CookieSettingsDialog open={showSettings} onOpenChange={setShowSettings} />
    </>
  );
}

// Dialog component for cookie settings
function CookieSettingsDialog({ 
  open, 
  onOpenChange 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void 
}) {
  const [consent, setConsent] = useState<CookieConsent>(defaultConsent);

  // Load saved consent when dialog opens
  useEffect(() => {
    if (open) {
      const savedConsent = localStorage.getItem('cookie-consent');
      if (savedConsent) {
        try {
          setConsent(JSON.parse(savedConsent));
        } catch (error) {
          console.error('Error parsing saved consent:', error);
        }
      }
    }
  }, [open]);

  // Handle checkbox changes
  const handleConsentChange = (key: keyof Omit<CookieConsent, 'timestamp'>) => {
    setConsent({
      ...consent,
      [key]: !consent[key]
    });
  };

  // Save current settings and close modal
  const saveSettings = () => {
    const updatedConsent: CookieConsent = {
      ...consent,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(updatedConsent));
    
    // Apply consent to analytics/marketing scripts
    applyConsent(updatedConsent);
    onOpenChange(false);
  };

  // Apply consent by enabling/disabling scripts
  const applyConsent = (consentSettings: CookieConsent) => {
    // Example: Enable/disable Google Analytics
    if (consentSettings.analytics) {
      // Code to initialize analytics
      console.log('Analytics enabled');
    } else {
      // Code to disable analytics
      console.log('Analytics disabled');
    }
    
    // Example: Enable/disable marketing cookies
    if (consentSettings.marketing) {
      // Code to initialize marketing tags
      console.log('Marketing enabled');
    } else {
      // Code to disable marketing tags
      console.log('Marketing disabled');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cookie-Einstellungen</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox id="essential" checked disabled />
            <div className="grid gap-1">
              <Label htmlFor="essential" className="font-medium">
                Essenzielle Cookies <span className="text-xs text-gray-500">(immer aktiv)</span>
              </Label>
              <p className="text-sm text-gray-500">
                Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
              </p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox 
              id="analytics" 
              checked={consent.analytics} 
              onCheckedChange={() => handleConsentChange('analytics')}
            />
            <div className="grid gap-1">
              <Label htmlFor="analytics" className="font-medium">
                Analyse-Cookies
              </Label>
              <p className="text-sm text-gray-500">
                Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.
              </p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox 
              id="marketing" 
              checked={consent.marketing} 
              onCheckedChange={() => handleConsentChange('marketing')}
            />
            <div className="grid gap-1">
              <Label htmlFor="marketing" className="font-medium">
                Marketing-Cookies
              </Label>
              <p className="text-sm text-gray-500">
                Diese Cookies werden verwendet, um Werbung relevanter für dich zu gestalten.
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={saveSettings} className="bg-[#2C6E49] hover:bg-[#1a5736]">
            Einstellungen speichern
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>(defaultConsent);

  // Load consent from localStorage on component mount
  useEffect(() => {
    const savedConsent = localStorage.getItem('cookie-consent');
    
    // If no consent is saved, show the banner
    if (!savedConsent) {
      setShowBanner(true);
      return;
    }
    
    try {
      const parsedConsent = JSON.parse(savedConsent);
      setConsent(parsedConsent);
    } catch (err) {
      console.error('Error parsing saved consent:', err);
      setShowBanner(true);
    }
  }, []);

  // Save consent to localStorage
  const saveConsent = (newConsent: CookieConsent) => {
    localStorage.setItem('cookie-consent', JSON.stringify(newConsent));
    setConsent(newConsent);
    
    // Apply consent to analytics/marketing scripts
    applyConsent(newConsent);
  };

  // Apply consent by enabling/disabling scripts
  const applyConsent = (consentSettings: CookieConsent) => {
    // Example: Enable/disable Google Analytics
    if (consentSettings.analytics) {
      // Code to initialize analytics
      console.log('Analytics enabled');
    } else {
      // Code to disable analytics
      console.log('Analytics disabled');
    }
    
    // Example: Enable/disable marketing cookies
    if (consentSettings.marketing) {
      // Code to initialize marketing tags
      console.log('Marketing enabled');
    } else {
      // Code to disable marketing tags
      console.log('Marketing disabled');
    }
  };

  // Accept all cookies
  const acceptAll = () => {
    const fullConsent: CookieConsent = {
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    saveConsent(fullConsent);
    setShowBanner(false);
  };

  // Reject non-essential cookies
  const rejectNonEssential = () => {
    const minimalConsent: CookieConsent = {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    saveConsent(minimalConsent);
    setShowBanner(false);
  };

  // Open cookie settings modal
  const openSettings = () => {
    setShowSettings(true);
    setShowBanner(false);
  };

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1E1E1E]/90 backdrop-blur-sm text-white p-4 shadow-lg border-t border-gray-700">
          <div className="container mx-auto max-w-screen-xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">Cookie-Einstellungen</h3>
                <p className="text-sm text-gray-300 mb-2">
                  Wir verwenden Cookies, um deine Erfahrung auf unserer Website zu verbessern.
                  Essenzielle Cookies sind für den Betrieb der Website erforderlich. 
                  Du kannst optionale Cookies akzeptieren oder ablehnen.
                </p>
                <div className="text-xs text-gray-400">
                  <span>Mehr Information in unserer </span>
                  <Link to="/datenschutz" className="text-[#2C6E49] hover:underline">Datenschutzerklärung</Link>
                  <span> und </span>
                  <Link to="/impressum" className="text-[#2C6E49] hover:underline">Impressum</Link>.
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2 md:mt-0">
                <Button 
                  variant="outline" 
                  onClick={openSettings}
                  className="text-white border-gray-600 hover:bg-gray-700"
                >
                  Einstellungen
                </Button>
                <Button 
                  variant="outline" 
                  onClick={rejectNonEssential}
                  className="text-white border-gray-600 hover:bg-gray-700"
                >
                  Ablehnen
                </Button>
                <Button onClick={acceptAll} className="bg-[#2C6E49] hover:bg-[#1a5736]">
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal - reusing the dialog component */}
      <CookieSettingsDialog 
        open={showSettings} 
        onOpenChange={setShowSettings} 
      />
    </>
  );
};

export default CookieBanner;
// We removed the duplicate exports here as they were already exported above
